import { ColorBlock } from "@/components/page";
import { useResourceList } from "@/components/data";

type WaitingListEntry = {
  id: number;
  student_name: string;
  age: string | null;
  school: string | null;
  parent_name: string | null;
  phone: string | null;
  desired_group: string | null;
  status: string | null;
  reason: string | null;
};

export function PaamonWaitingListPage() {
  const { data, isLoading } = useResourceList<WaitingListEntry>("paamon_waiting_list");
  const count = data?.length ?? 0;

  // DEM-40: Layout owns chrome (sky-blue bg + max-w-[1000px] on /paamon).
  return (
    <div className="space-y-6 sm:space-y-7">
        {/* Hero — number-as-headline */}
        <header className="pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            רשימת המתנה
          </p>
          <h1 className="mt-3 flex items-baseline gap-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
            <span className="tabular-nums" dir="ltr">{count}</span>
            <span className="text-stone-300 text-lg sm:text-xl">·</span>
            <span className="text-stone-700 text-lg sm:text-xl">ממתינים</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
            תלמידים בהמתנה לשיבוץ קבוצה.
          </p>
        </header>

        {isLoading ? (
          <p className="text-sm text-stone-500">טוען…</p>
        ) : count === 0 ? (
          <ColorBlock tint="sage" className="text-center" size="lg">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-600">
              ריק
            </p>
            <p className="mt-3 text-xl sm:text-2xl font-bold tracking-tight text-stone-900">
              אין כעת ממתינים.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed max-w-md mx-auto">
              ברגע שיירשם תלמיד שלא ניתן לשבץ מיד, הוא יופיע כאן עם פרטי הקשר וסיבת ההמתנה.
            </p>
          </ColorBlock>
        ) : (
          <div className="rounded-[24px] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["שם תלמיד", "גיל", "בית ספר", "הורה", "טלפון", "קבוצה רצויה", "סטטוס", "סיבה"].map((h) => (
                    <th key={h} className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((w) => (
                  <tr key={w.id} className="border-t border-stone-200/70 hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-stone-900">{w.student_name}</td>
                    <td className="px-6 py-4 text-stone-600">{w.age ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{w.school ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{w.parent_name ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600 tabular-nums" dir="ltr">{w.phone ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{w.desired_group ?? "—"}</td>
                    <td className="px-6 py-4">
                      {w.status && (
                        <span className="inline-flex items-center gap-2 text-stone-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                          {w.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-stone-500">{w.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
